<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Generic guest gate — shown when an unauthenticated visitor tries
 * any locked action that doesn't have a more specific guest variant.
 */

const { t } = useI18n();
const { percent: topSplitPercent } = useTopSplitPercent();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'signup-click'): void;
  (e: 'signin-click'): void;
}>();

const titleId = useId();
</script>

<template>
  <ModalShell
    accent="gold"
    :labelled-by="titleId"
    @close="emit('close')"
  >
    <ModalIcon
      kind="lock"
      accent="gold"
    />
    <div class="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-gold-primary-120">
      {{ t('property.access.modalGuestGeneric.eyebrow') }}
    </div>
    <h2
      :id="titleId"
      class="mb-3 font-serif text-[24px] font-semibold leading-[1.15] tracking-[-0.015em] text-black-100"
    >
      <span>{{ t('property.access.modalGuestGeneric.headingLine1') }}</span
      ><br /><span>{{ t('property.access.modalGuestGeneric.headingLine2') }}</span>
    </h2>
    <p class="mb-5 text-[13.5px] leading-[1.55] text-black-70">
      {{ t('property.access.modalGuestGeneric.body', { percent: topSplitPercent }) }}
    </p>
    <ul class="mb-6 space-y-2">
      <CheckBullet accent="gold">{{ t('property.access.modalGuestGeneric.bullet1') }}</CheckBullet>
      <CheckBullet accent="gold">{{
        t('property.access.modalGuestGeneric.bullet2', { percent: topSplitPercent })
      }}</CheckBullet>
      <CheckBullet accent="gold">{{ t('property.access.modalGuestGeneric.bullet3') }}</CheckBullet>
      <CheckBullet accent="gold">{{ t('property.access.modalGuestGeneric.bullet4') }}</CheckBullet>
      <CheckBullet accent="gold">{{ t('property.access.modalGuestGeneric.bullet5') }}</CheckBullet>
      <CheckBullet accent="gold">{{ t('property.access.modalGuestGeneric.bullet6') }}</CheckBullet>
    </ul>
    <div class="flex flex-col gap-2">
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center rounded-[10px] bg-black-100 px-5 text-[14px] font-bold text-white transition-colors hover:bg-black-90"
        @click="emit('signup-click')"
      >
        <span
          >{{ t('property.access.modalGuestGeneric.signUp') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></span
        >
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center rounded-[10px] text-[13px] font-semibold text-black-80 transition-colors hover:bg-black-05"
        @click="emit('signin-click')"
      >
        {{ t('property.access.modalGuestGeneric.alreadyHaveAccount') }}
      </button>
    </div>
    <div class="mt-4 text-center text-[11.5px] text-black-60">
      {{ t('property.access.modalGuestGeneric.footNote') }}
    </div>
  </ModalShell>
</template>
